import React from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  Stack,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, gql, useMutation } from "@apollo/client";
import { IMAGES_API_URL } from "../constants";
import { Controller, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { AddCircle, Delete, Edit } from "@mui/icons-material";

export default function RoleLinkPage() {
  const { eventId, role } = useParams();
  const [add, setAdd] = React.useState(false);

  const { data, error, refetch } = useQuery(GET_LINK, {
    variables: { eventId, role },
  });
  const [updateLink] = useMutation(UPDATE_LINK);
  const [createLink] = useMutation(CREATE_LINK);
  const [deleteLink] = useMutation(DELETE_LINK);

  // "" in the presentation selects means "inherit / default" — send NULL so the DB keeps no
  // opinion and every existing link behaves exactly as it did before this feature.
  const normalizeLinkInput = (f) => ({
    ...f,
    headerMode: f.headerMode ? f.headerMode : null,
    headerImageUrl:
      f.headerMode === "custom" && f.headerImageUrl ? f.headerImageUrl : null,
    onlyPmt: f.onlyPmt === "on" ? true : f.onlyPmt === "off" ? false : null,
    /* "" is the operator saying "let the role's own approval workflow decide" — the same way the
       header selects represent "inherit". api-admin normalises it to NULL. */
    statusOverride: f.statusOverride ? f.statusOverride : null,
  });

  const onUpdate = async (f) => {
    return updateLink({
      variables: {
        inp: {
          eventId,
          role,
          ...normalizeLinkInput(f),
        },
      },
    })
      .then((res) => refetch())
      .then(() => true)
      .catch((e) => alert(JSON.stringify(e, null, 2)));
  };

  const onCreate = async (f) => {
    return createLink({
      variables: {
        inp: {
          eventId,
          role,
          ...normalizeLinkInput(f),
        },
      },
    })
      .then((res) => refetch())
      .then(() => true)
      .catch((e) => alert(JSON.stringify(e, null, 2)));
  };
  const onDelete = (id) => {
    if (window.confirm("Are you sure?")) {
      deleteLink({ variables: { id } }).then(() => refetch());
    }
  };

  return (
    <Box>
      <AddLinkDialog
        eventId={eventId}
        open={add}
        onClose={() => setAdd(false)}
        onSubmit={async (f) => {
          if (await onCreate(f)) {
            setAdd(false);
          }
        }}
      />

      <Typography
        variant="h4"
        gutterBottom
        sx={{ textTransform: "capitalize" }}
      >
        {role} Registration Links
      </Typography>
      {!!error && (
        <Typography color="error">
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Typography>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableCell>#</TableCell>
            <TableCell>Link</TableCell>
            <TableCell>Form Heading</TableCell>
            <TableCell>Active</TableCell>
            <TableCell>Actions</TableCell>
          </TableHead>
          <TableBody>
            {data?.links?.map((l, i) => (
              <LinkRow
                link={l}
                key={i}
                i={i + 1}
                eventId={eventId}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        variant="contained"
        color="success"
        sx={{ mt: 4 }}
        startIcon={<AddCircle />}
        onClick={() => setAdd(true)}
      >
        Add New Link
      </Button>

      <Box p={4} />
    </Box>
  );
}

function LinkRow({ link, i, onUpdate, onDelete, eventId }) {
  const [edit, setEdit] = React.useState(false);
  return (
    <React.Fragment>
      <EditLinkDialog
        eventId={eventId}
        link={link}
        open={edit}
        onClose={() => setEdit(false)}
        onSubmit={async (f) => {
          if (await onUpdate(f)) {
            setEdit(false);
          }
        }}
      />

      <TableRow>
        <TableCell>{i}</TableCell>
        <TableCell>
          <Link href={`https://eventegration.com/${link.link}`}>
            https://eventegration.com/{link.link}
          </Link>
        </TableCell>
        <TableCell>{link.formHeading}</TableCell>
        <TableCell>{link.active ? "Yes" : "No"}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={() => setEdit(true)}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => onDelete(link.id)}>
              <Delete />
            </IconButton>
          </Stack>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function EditLinkDialog({ open, onClose, link, onSubmit, eventId }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Link</DialogTitle>
      <DialogContent>
        <Box p={1}>
          {!!link && <LinkSetup link={link} onSubmit={onSubmit} eventId={eventId} />}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
function AddLinkDialog({ open, onClose, onSubmit, eventId }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add New Link</DialogTitle>
      <DialogContent>
        <Box p={1}>
          <LinkSetup onSubmit={onSubmit} eventId={eventId} />
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function LinkSetup({ onSubmit, link, eventId }) {
  const { control, formState, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      link: "",
      formHeading: "",
      // "" = inherit / default. Stored as NULL so an untouched link behaves exactly as before.
      headerMode: "",
      headerImageUrl: "",
      onlyPmt: "",
      statusOverride: "",
      active: false,
    },
  });
  React.useEffect(() => {
    if (link?.link) {
      const { __typename, ...formData } = link;
      // NULL means "inherit / default" — the selects represent that as "".
      reset({
        ...formData,
        headerMode: formData.headerMode ?? "",
        headerImageUrl: formData.headerImageUrl ?? "",
        onlyPmt:
          formData.onlyPmt === true ? "on" : formData.onlyPmt === false ? "off" : "",
        statusOverride: formData.statusOverride ?? "",
      });
    }
  }, [link]);

  const headerMode = watch("headerMode");

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={2}>
        <Controller
          control={control}
          name="link"
          rules={{ required: "Required", maxLength: 500 }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField
              inputRef={ref}
              {...field}
              required
              error={!!error}
              helperText={error?.message}
              fullWidth
              label="Link"
            />
          )}
        />
        <Controller
          control={control}
          name="formHeading"
          rules={{ required: "Required", maxLength: 500 }}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField
              inputRef={ref}
              {...field}
              required
              error={!!error}
              helperText={error?.message}
              fullWidth
              label="Form Heading"
            />
          )}
        />
        {/* Per-link presentation (owner 2026-09-20). Previously the only way to give one link a
            different header was another hardcoded event id in www's RegistrationPage. */}
        <Controller
          control={control}
          name="headerMode"
          render={({ field: { ref, ...field } }) => (
            <TextField
              inputRef={ref}
              {...field}
              select
              fullWidth
              label="Header above the form"
              helperText="Default = the event banner. Choose Custom image to show a different logo/artwork on this link only."
            >
              <MenuItem value="">Event banner (default)</MenuItem>
              <MenuItem value="none">No image</MenuItem>
              <MenuItem value="custom">Custom image</MenuItem>
            </TextField>
          )}
        />
        {headerMode === "custom" && (
          <Controller
            control={control}
            name="headerImageUrl"
            rules={{ required: "Required when the header is a custom image", maxLength: 500 }}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Stack spacing={1}>
                <TextField
                  inputRef={ref}
                  {...field}
                  error={!!error}
                  helperText={
                    error?.message ||
                    "Upload the image, or paste a full URL if it is already hosted somewhere."
                  }
                  fullWidth
                  label="Custom header image"
                />
                <HeaderImageUpload
                  eventId={eventId}
                  value={field.value}
                  onUploaded={(url) => field.onChange(url)}
                />
              </Stack>
            )}
          />
        )}
        <Controller
          control={control}
          name="onlyPmt"
          render={({ field: { ref, ...field } }) => (
            <TextField
              inputRef={ref}
              {...field}
              select
              fullWidth
              label="Invitation only (onlyPMT)"
              helperText="Default follows the role's onlyPMT feature. Override it for THIS link only."
            >
              <MenuItem value="">Follow the role's feature (default)</MenuItem>
              <MenuItem value="on">On — this link needs an invitation (pmt/uid)</MenuItem>
              <MenuItem value="off">Off — this link is open</MenuItem>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="statusOverride"
          render={({ field: { ref, ...field } }) => (
            <TextField
              inputRef={ref}
              {...field}
              select
              fullWidth
              label="Approval decision for this link"
              helperText={
                "Skips the role's keyword approval rules and stamps this decision at registration. " +
                "Only has an effect on a role whose approval workflow (emailVerification) is on. " +
                "A paid registration and an attendee already rejected are never overridden."
              }
            >
              <MenuItem value="">Let the role's approval rules decide (default)</MenuItem>
              <MenuItem value="accept">Accept — approve everyone on this link</MenuItem>
              <MenuItem value="pending">Pending — hold everyone on this link for review</MenuItem>
              <MenuItem value="reject">Reject — decline everyone on this link</MenuItem>
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="active"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={field.onChange} />
              }
              label="Active"
            />
          )}
        />

        <Stack direction="row" spacing={2}>
          <LoadingButton
            loading={formState.isSubmitting}
            type="submit"
            color="primary"
            variant="contained"
          >
            Save
          </LoadingButton>
          {!!link?.link && (
            <Button variant="contained" color="error" onClick={() => reset()}>
              Delete
            </Button>
          )}
        </Stack>
      </Stack>
    </form>
  );
}


/**
 * Upload the link's custom header image (owner 2026-09-20). The mutation returns a path relative
 * to the public root; we store the ABSOLUTE url in the link row, because www renders it directly
 * and api-reg only passes the value through. Upload does NOT save the link — the URL lands in the
 * form field and is written with the rest of the form, so upload-then-Cancel changes nothing.
 */
function HeaderImageUpload({ eventId, value, onUploaded }) {
  const [upload, { loading }] = useMutation(UPLOAD_HEADER_IMAGE);
  const [err, setErr] = React.useState(null);

  async function onChange({ target }) {
    const file = target.files && target.files[0];
    target.value = ""; // let the same file be picked again after a failure
    if (!file) return;
    setErr(null);
    try {
      const res = await upload({ variables: { eventId, file } });
      const relPath = res?.data?.uploadLinkHeaderImage;
      if (!relPath) throw new Error("Upload did not return a path.");
      onUploaded(IMAGES_API_URL + relPath);
    } catch (e) {
      setErr(e.message || "Upload failed.");
    }
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Button variant="outlined" component="label" disabled={loading}>
        {loading ? "Uploading…" : "Upload image"}
        <input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={onChange} />
      </Button>
      {!!value && (
        <img
          src={value}
          alt="Header preview"
          style={{ maxHeight: 56, maxWidth: 220, objectFit: "contain" }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      )}
      <Typography variant="caption" color={err ? "error" : "text.secondary"}>
        {err || "PNG, JPG or WEBP · up to 2 MB"}
      </Typography>
    </Stack>
  );
}

const GET_LINK = gql`
  query ($eventId: String!, $role: String!) {
    links(eventId: $eventId, role: $role) {
      id
      link
      active
      formHeading
      headerMode
      headerImageUrl
      onlyPmt
      statusOverride
    }
  }
`;

const UPDATE_LINK = gql`
  mutation ($inp: InputEventLink!) {
    updateLink(inp: $inp) {
      id
    }
  }
`;
const CREATE_LINK = gql`
  mutation ($inp: InputEventLink!) {
    createLink(inp: $inp) {
      id
    }
  }
`;
const UPLOAD_HEADER_IMAGE = gql`
  mutation ($eventId: String!, $file: Upload!) {
    uploadLinkHeaderImage(eventId: $eventId, file: $file)
  }
`;
const DELETE_LINK = gql`
  mutation ($id: Int!) {
    deleteLink(id: $id)
  }
`;
